import { ConversationParticipantEntity } from '@provision/conversation-types';
import type { SupportPolicy } from '@provision/entity-types';
import { UrlParams } from '@provision/portal-routes';
import { ServiceLevel } from '@provision/service-level-utils';
import {
  conditionsReferenceCustomProperty,
  isPolicyApplicable,
  parseCustomAttributesBlob,
  PolicyCustomPropertyDefinitions,
  PolicyEvaluationContext,
} from '@provision/sidekick-utils';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  useCurrentUserProfile,
  useEntityAssignments,
  useGroupRules,
  usePartnerGroupsOptions,
  UserGroupRules,
} from 'src/graphql';
import { useAddressRecord } from 'src/graphql/entities/address/address';
import { getCustomAttributesConfig } from 'src/graphql/entities/custom-attributes/custom-attributes-config/custom-attributes-config';
import { NormalizedCustomAttributeDefinition } from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { useNormalizedCustomAttributeDefinitions } from 'src/graphql/entities/custom-attributes/hooks/use-normalized-custom-attribute-definitions';
import { getNotificationEntityName } from 'src/graphql/entities/notification/notification-target';
import {
  NOTIFICATION_ENTITY_KEY_SEPARATOR,
  parseNotificationCompositeKey,
} from 'src/graphql/entities/notification/utils';
import { useManageUrgencyLevel } from 'src/graphql/entities/signal-business-hours/hooks/use-manage-urgency-level';
import { useSupportPolicies } from 'src/graphql/entities/support-policy';
import {
  buildSupportPolicyPartnerFilterValue,
  NormalizedSupportPolicy,
} from 'src/graphql/entities/support-policy/support-policy.utils';
import { EntityNames } from 'src/graphql/types';
import { useSelectedConversation as useConversationDetails } from 'src/pages/Conversations';
import { useSelectedConversation as useSelectedConversationStore } from 'src/store/selected-conversation';
import { useRouteParams } from 'src/utils/routing';
import {
  getApplicablePolicyServiceLevel,
  getPolicyAddressLabelAssignments,
} from './policies-tab.applicability';
import { resolvePartnerId } from './policies-tab.utils';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';
import { useEscalationEvaluation } from './use-escalation-evaluation';
import { useNewEscalationPoliciesFlag } from './use-escalation-flag';

interface PolicyAddressContext {
  addressId?: string | null;
  partnerId?: number | null;
  primaryClientId?: string | null;
  brandId?: string | null;
  brand?: { brandId?: string | null; id?: string | null; name?: string | null } | null;
}

const NO_ADDRESS_IDS: readonly string[] = [];

const collectCustomPropertyDefinitions = (
  definitions: NormalizedCustomAttributeDefinition[],
): Record<string, { type: string; defaultValue?: unknown }> =>
  Object.fromEntries(
    definitions.map((definition) => [
      definition.key,
      { type: definition.type, defaultValue: definition.defaultValue },
    ]),
  );

const toSupportPolicyRecord = (policy: NormalizedSupportPolicy): SupportPolicy => ({
  ...policy,
  addressIds: JSON.stringify(policy.addressIds ?? []),
  clientIds: JSON.stringify(policy.clientIds ?? []),
  clientToAddressIds: JSON.stringify(policy.clientToAddressIds ?? []),
});

export interface UseApplicablePoliciesParams {
  /** Used when there is no entity context and no explicit scope. */
  fallbackPartnerId: number;
  /** Set, it outranks the open site or conversation. */
  scopePartnerId?: number | null;
  showArchived?: boolean;
}

export interface UseApplicablePoliciesResult {
  /** Unfiltered — still needed for the actions menu and escalation seeding. */
  supportPolicies: NormalizedSupportPolicy[];
  /** Permission/archive/urgency-filtered set. */
  accessiblePolicies: NormalizedSupportPolicy[];
  applicablePolicies: NormalizedSupportPolicy[];
  policyEvaluationContext: PolicyEvaluationContext;
  /** scope > site > conversation > fallback; undefined while a site's address loads. */
  partnerId: number | undefined;
  escalationEval: ReturnType<typeof useEscalationEvaluation>;
  canAccessEscalationPolicies: boolean;
  canManageEscalationPolicies: boolean;
  canManagePolicies: boolean;
  isLoading: boolean;
}

export const useApplicablePolicies = (
  params: UseApplicablePoliciesParams,
): UseApplicablePoliciesResult => {
  const { fallbackPartnerId, scopePartnerId, showArchived = false } = params;

  const { user } = useCurrentUserProfile();

  const { checkValidity } = useGroupRules();

  const canManagePolicies = checkValidity([
    UserGroupRules.AccessToManageSupportPoliciesInAssistant,
  ]);
  const canManageEscalationPolicies = checkValidity([
    UserGroupRules.AccessToNewEscalationPolicyManagement,
  ]);

  const { conversationId: selectedConversationId } = useSelectedConversationStore();
  const { selectedConversation } = useConversationDetails({
    conversationId: selectedConversationId ?? '',
  });

  // from the URL, not tab state, so a directly-opened site page is recognised too
  const { recordId: rawRecordId } = useRouteParams(UrlParams.recordId);
  // on the inbox preview route (`/inbox/:recordId`) this same param is a notification
  // composite key (e.g. `Site__<addressId>`), not a plain addressId — unwrap it, and
  // drop it for any non-Site entity so we don't feed a conversation/project id in here
  const activeSiteAddressId = useMemo(() => {
    if (!rawRecordId?.includes(NOTIFICATION_ENTITY_KEY_SEPARATOR)) {
      return rawRecordId;
    }
    const parsed = parseNotificationCompositeKey(rawRecordId);
    return parsed && getNotificationEntityName(parsed.entity) === EntityNames.Site
      ? parsed.entityId
      : undefined;
  }, [rawRecordId]);

  const { address: activeSiteAddress, isLoading: isLoadingAddress } = useAddressRecord({
    addressId: activeSiteAddressId ?? '',
  });

  const conversationAddressIds = useMemo(
    () =>
      selectedConversation?.associatedAddresses
        ?.map((address) => address?.addressId)
        .filter((addressId): addressId is string => Boolean(addressId)) ?? [],
    [selectedConversation?.associatedAddresses],
  );

  const { labelAssignmentsMap: conversationAddressLabelAssignmentsMap } = useEntityAssignments(
    {
      entity: 'Address',
      ids: conversationAddressIds,
      skip: !conversationAddressIds.length,
    },
  );

  const partnerId = useMemo(
    () =>
      resolvePartnerId({
        scopePartnerId,
        siteAddressPartnerId: activeSiteAddress?.partnerId ?? undefined,
        conversationPartnerId: selectedConversation?.partnerId ?? undefined,
        fallbackPartnerId,
        // waiting for the address avoids a duplicate query
        isSitePartnerPending: Boolean(activeSiteAddressId) && isLoadingAddress,
      }),
    [
      activeSiteAddressId,
      isLoadingAddress,
      activeSiteAddress?.partnerId,
      selectedConversation?.partnerId,
      scopePartnerId,
      fallbackPartnerId,
    ],
  );

  const partnerFilterValue = useMemo(
    () => buildSupportPolicyPartnerFilterValue(partnerId),
    [partnerId],
  );

  const canSeeEscalationRuntime = useNewEscalationPoliciesFlag(partnerId);
  const partnerGroupOptions = usePartnerGroupsOptions({
    partnerId: partnerId ?? null,
  });

  const canAccessEscalationPolicies = canSeeEscalationRuntime || canManageEscalationPolicies;

  const contextServiceLevel = useMemo(() => {
    if (selectedConversation?.associatedAddresses?.length) {
      for (const address of selectedConversation.associatedAddresses) {
        const serviceLevel = address ? getApplicablePolicyServiceLevel(address) : null;

        if (serviceLevel) {
          return serviceLevel;
        }
      }
    }
    if (activeSiteAddress) {
      const serviceLevel = getApplicablePolicyServiceLevel(activeSiteAddress);
      if (serviceLevel) {
        return serviceLevel;
      }
    }
    return ServiceLevel.NonMember;
  }, [selectedConversation?.associatedAddresses, activeSiteAddress]);

  const escalationEval = useEscalationEvaluation(partnerId, contextServiceLevel);

  const { supportPolicies, isLoading } = useSupportPolicies(
    {
      filters: [
        {
          attribute: 'partnerId',
          operator: 'in',
          value: partnerFilterValue,
        },
      ],
    },
    {
      skip: partnerId === undefined,
    },
  );

  const userGroupIds = useMemo(
    () =>
      user?.userGroups
        ?.map((group) => group.id ?? group.userGroupId)
        .filter((id): id is string => Boolean(id)) ?? [],
    [user?.userGroups],
  );

  const [disabledUrgencies] = useManageUrgencyLevel();

  const accessiblePolicies = useMemo(() => {
    let policies = supportPolicies;

    if (!showArchived) {
      policies = policies.filter((p) => !p.archivedAt);
    }

    if (!canManagePolicies) {
      policies = policies.filter((p) => {
        const policyGroupIds =
          p.userGroups?.map((group) => group.id ?? group.userGroupId).filter(Boolean) ?? [];

        if (policyGroupIds.length === 0) {
          return true;
        }

        return policyGroupIds.some((id) => userGroupIds.includes(id));
      });
    }

    if (!canAccessEscalationPolicies) {
      policies = policies.filter((p) => p.category !== POLICY_CATEGORY_ESCALATION);
    }

    if (disabledUrgencies.length > 0) {
      const disabledSet = new Set<string>(disabledUrgencies);
      policies = policies.filter((p) => {
        if (p.category !== POLICY_CATEGORY_ESCALATION || !p.urgencyLevel) return true;
        return !disabledSet.has(p.urgencyLevel);
      });
    }

    return policies;
  }, [
    supportPolicies,
    showArchived,
    userGroupIds,
    canManagePolicies,
    canAccessEscalationPolicies,
    disabledUrgencies,
  ]);

  // the sites whose custom properties a policy condition can read: the open conversation's linked
  // sites, or the open site page; the queries stay dormant when neither is in context
  const policyAddressIds = useMemo(() => {
    if (selectedConversation) return conversationAddressIds;
    return activeSiteAddressId ? [activeSiteAddressId] : [];
  }, [selectedConversation, conversationAddressIds, activeSiteAddressId]);

  const { useStoredAttributes: useConversationStoredAttributes } =
    getCustomAttributesConfig('Conversation');
  const { useStoredAttributesByIds: useAddressStoredAttributesByIds } =
    getCustomAttributesConfig('Address');

  // only partners with at least one custom-property policy pay for the blob queries; everyone
  // else keeps the pre-existing no-skeleton behaviour on conversation switches
  const needsCustomPropertyBlobs = useMemo(
    () =>
      accessiblePolicies.some((policy) =>
        conditionsReferenceCustomProperty(policy.conditions),
      ),
    [accessiblePolicies],
  );

  const {
    customAttributes: conversationCustomAttributesJson,
    loading: isLoadingConversationCustomAttributes,
  } = useConversationStoredAttributes(
    needsCustomPropertyBlobs ? selectedConversation?.conversationId : undefined,
  );
  const {
    customAttributesById: addressCustomAttributesById,
    loading: isLoadingAddressCustomAttributes,
  } = useAddressStoredAttributesByIds(
    needsCustomPropertyBlobs ? policyAddressIds : NO_ADDRESS_IDS,
  );

  // a record that never saved a property reads as the definition's default — the value the side
  // panel shows for it — so conditions match what the author saw. Definitions restricted to another
  // user group count too: applicability must not depend on who is looking.
  const definitionsPartnerId = needsCustomPropertyBlobs
    ? (partnerId ?? fallbackPartnerId)
    : undefined;

  const conversationDefinitions = useNormalizedCustomAttributeDefinitions({
    partnerId: definitionsPartnerId,
    entity: 'Conversation',
  });

  const addressDefinitions = useNormalizedCustomAttributeDefinitions({
    partnerId: definitionsPartnerId,
    entity: 'Address',
  });

  const customPropertyDefinitions = useMemo<PolicyCustomPropertyDefinitions>(
    () => ({
      Conversation: collectCustomPropertyDefinitions(conversationDefinitions.allDefinitions),
      Address: collectCustomPropertyDefinitions(addressDefinitions.allDefinitions),
    }),
    [conversationDefinitions.allDefinitions, addressDefinitions.allDefinitions],
  );

  const policyEvaluationContext = useMemo<PolicyEvaluationContext>(() => {
    const addressIds = new Set<string>();
    const clientIds = new Set<string>();
    const clientToAddressIds = new Set<string>();
    const urgencies = new Set<string>();
    const siteLabelIds = new Set<string>();
    const serviceLevels = new Set<string>();
    const clientSegmentIds = new Set<string>();
    const conversationTypes = new Set<string>();
    const partnerGroupIds = new Set<string>();
    const resolvedPartnerId = partnerId ?? fallbackPartnerId;
    const partnerIds = new Set<string>([
      String(resolvedPartnerId),
      String(PLATFORM_PARTNER_ID),
    ]);

    const addValue = (set: Set<string>, value?: string | number | null): void => {
      if (value === null || value === undefined || value === '') return;
      set.add(String(value));
    };

    const addAddressContext = (
      address: PolicyAddressContext,
      serviceLevel?: string | null,
      labelIds: (string | null | undefined)[] = [],
    ): void => {
      addValue(addressIds, address.addressId);
      addValue(partnerIds, address.partnerId);
      addValue(clientIds, address.primaryClientId);
      addValue(serviceLevels, serviceLevel);
      addValue(
        clientSegmentIds,
        address.brandId ?? address.brand?.brandId ?? address.brand?.id,
      );
      addValue(clientSegmentIds, address.brand?.name ?? null);
      labelIds.forEach((labelId) => addValue(siteLabelIds, labelId));
    };

    partnerGroupOptions.forEach((option) =>
      addValue(partnerGroupIds, option.value ?? option.id),
    );

    if (selectedConversation) {
      if (selectedConversation.type) {
        addValue(conversationTypes, selectedConversation.type);
        addValue(conversationTypes, selectedConversation.type.toLowerCase());
      }

      selectedConversation.associatedAddresses?.forEach((address) => {
        if (!address) return;
        addAddressContext(
          address,
          getApplicablePolicyServiceLevel(address),
          getPolicyAddressLabelAssignments(
            address,
            conversationAddressLabelAssignmentsMap,
          ).map((assignment) => assignment?.labelId ?? null),
        );
      });

      const requesterEntity = selectedConversation.requester?.participantEntity;
      const requesterIdentity = selectedConversation.requester?.participantIdentity;
      if (
        requesterEntity &&
        requesterIdentity &&
        requesterEntity.toLowerCase() === String(ConversationParticipantEntity.Client)
      ) {
        addValue(clientIds, requesterIdentity);
      }

      if (selectedConversation.urgency) {
        urgencies.add(selectedConversation.urgency);
        urgencies.add(selectedConversation.urgency.toLowerCase());
      }
    } else if (activeSiteAddress) {
      addAddressContext(
        activeSiteAddress,
        getApplicablePolicyServiceLevel(activeSiteAddress),
        (activeSiteAddress.labelAssignments ?? []).map(
          (assignment) => assignment?.labelId ?? null,
        ),
      );
      // no conversation context here, so conversationTypes and urgencies stay empty
    }

    return {
      addressIds,
      clientIds,
      clientToAddressIds,
      urgencies,
      partnerIds,
      siteLabelIds,
      serviceLevels,
      clientSegmentIds,
      conversationTypes,
      partnerGroupIds,
      conversationCustomAttributes: selectedConversation
        ? [parseCustomAttributesBlob(conversationCustomAttributesJson)]
        : [],
      addressCustomAttributes: policyAddressIds.map((addressId) =>
        parseCustomAttributesBlob(addressCustomAttributesById.get(addressId)),
      ),
      customPropertyDefinitions,
      partnerId: resolvedPartnerId,
      userGroupIds,
      currentTime: escalationEval.currentTime.toDate(),
      timezone: escalationEval.timezone,
      businessHours: escalationEval.businessHours,
      escalationCutoffHours: escalationEval.escalationCutoffHours,
      holidays: escalationEval.holidays,
    };
  }, [
    partnerId,
    fallbackPartnerId,
    selectedConversation,
    activeSiteAddress,
    conversationAddressLabelAssignmentsMap,
    conversationCustomAttributesJson,
    policyAddressIds,
    addressCustomAttributesById,
    customPropertyDefinitions,
    userGroupIds,
    partnerGroupOptions,
    escalationEval.currentTime,
    escalationEval.timezone,
    escalationEval.businessHours,
    escalationEval.escalationCutoffHours,
    escalationEval.holidays,
  ]);

  const applicablePolicies = useMemo(
    () =>
      accessiblePolicies.filter((policy) =>
        isPolicyApplicable(toSupportPolicyRecord(policy), policyEvaluationContext),
      ),
    [accessiblePolicies, policyEvaluationContext],
  );

  return {
    supportPolicies,
    accessiblePolicies,
    applicablePolicies,
    policyEvaluationContext,
    partnerId,
    escalationEval,
    canAccessEscalationPolicies,
    canManageEscalationPolicies,
    canManagePolicies,
    isLoading:
      isLoading ||
      isLoadingConversationCustomAttributes ||
      isLoadingAddressCustomAttributes ||
      conversationDefinitions.loading ||
      addressDefinitions.loading,
  };
};
